// Vendor Libs
import React, {useState} from 'react';

// Material UI
import {Box, Button, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import DeleteIcon from '@material-ui/icons/Delete';

// Dish Tech React
import {InputTextField} from '@dishtech-react/core';
import {useForm, useOverlay, useToast} from '@dishtech-react/hooks';

// Configs
import {serviceConfig} from '../../../config/service-config';

// Hooks
import {useFolder} from '../../hooks/use-folder';

const FORM_KEY = 'FOLDER';

const useStyles = makeStyles(theme => ({
  border: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  button: {
    marginBottom: theme.spacing(1),
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  tooltip: {
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[1],
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: 15,
  },
  box: {
    minWidth: '500px',
    marginTop: theme.spacing(1),
  },
  subFolderText: {
    fontWeight: 'bold',
  },
  text: {
    marginBottom: theme.spacing(1),
  },
  warning: {
    color: 'red',
    marginBottom: theme.spacing(1),
  },
  error: {
    color: 'red',
    marginBottom: theme.spacing(1),
  },
}));

const DeleteFolderConfirmation = () => {
  const classes = useStyles();
  const {submitForm} = useForm(serviceConfig, FORM_KEY, 'delete');
  const [showError, setShowError] = useState(false);
  const [folderName, setFolderName] = useState('');
  const {createToast} = useToast();
  const {closeOverlay} = useOverlay({id: 'folder_delete'});
  const {folderToDelete = {}} = useFolder();
  const {name, folderID, subFolders = []} = folderToDelete;

  const getDescendants = ({name: n, subFolders: s}) => [n, ...s.flatMap(getDescendants)];

  const getSubFolderNames = () => [...new Set(subFolders.flatMap(getDescendants))];

  const handleDelete = () => {
    if (folderName === name) {
      submitForm({}, folderID).then(({error}) => {
        if (error) {
          createToast(error, {
            variant: 'error',
            persist: true,
            onClose: () => closeOverlay(),
          });
        } else {
          createToast('Successfully deleted.');
          closeOverlay();
        }
      });
    } else {
      setShowError(true);
    }
  };

  return (
    <>
      {subFolders.length ? (
        <Typography className={classes.warning} variant="body1">
          Warning: folder &apos;<b>{name}</b>&apos; and the following sub-folders will be deleted.
        </Typography>
      ) : (
        <Typography className={classes.warning} variant="body1">
          Warning: folder &apos;<b>{name}</b>&apos; will be deleted.
        </Typography>
      )}

      {subFolders.length ? (
        <Box className={classes.box}>
          <ul style={{listStyleType: 'none'}}>
            {getSubFolderNames().map(subfolderName => (
              <li key={subfolderName}>
                <Typography className={classes.subFolderText} variant="body1">
                  {subfolderName}
                </Typography>
              </li>
            ))}
          </ul>
        </Box>
      ) : null}

      <Typography className={classes.text} variant="body1">
        Type the name of folder <b>{name}</b> to confirm deletion:
      </Typography>

      <InputTextField
        autoFocus={false}
        className={classes.border}
        fullWidth={true}
        id="folderName"
        label="Folder Name"
        margin="dense"
        type="text"
        value={folderName}
        onChange={event => setFolderName(event.target.value)}
      />

      {showError ? (
        <Typography className={classes.error} variant="body1">
          Please enter exact folder name
        </Typography>
      ) : null}

      <Button
        className={classes.button}
        color="default"
        startIcon={<CancelIcon />}
        variant="contained"
        onClick={closeOverlay}
      >
        Cancel
      </Button>

      <Button
        className={classes.button}
        color="primary"
        disabled={folderName.length === 0}
        startIcon={<DeleteIcon />}
        variant="contained"
        onClick={handleDelete}
      >
        Delete
      </Button>
    </>
  );
};

export default DeleteFolderConfirmation;
